import React from 'react';
import cn from 'classnames';
import _toLower from 'lodash/toLower';
import styles from './index.module.scss';

import { UiKit } from 'components';
import { useId } from 'utils/hooks';

/**
 * @param {import('react').InputHTMLAttributes<HTMLInputElement> & {
 *  inputRef?: React.Ref<HTMLInputElement>
 *  size?: import('utils/types').Size
 *  type?: "text" | "number" | "password" | "email"
 *  label?: string
 *  caption?: string
 *  tooltipText?: string
 *  value?: string
 *  onChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void
 *  error?: string
 *  disabled?: boolean
 *  icon?: React.ReactNode
 *  maxDecimalPrecision?: number
 *  bypassReadMode?: boolean
 *  className?: string
 *  inputWrapperClassName?: string
 *  inputClassName?: string
 * }} props
 */
export default function Input({
  id = '',
  size = 'md',
  type = 'text',
  label = '',
  caption = '',
  tooltipText = '',
  value = '',
  onChange = () => {},
  error = '',
  disabled = false,
  icon = null,
  maxDecimalPrecision = 2,
  bypassReadMode = false,
  className = '',
  inputWrapperClassName = '',
  inputClassName = '',
  children = null,
  inputRef = null,
  ...inputHtmlProps
}) {
  const internalId = useId();

  const inputId = id || internalId;
  const isNumberInput = type === 'number';
  const allowDecimal = maxDecimalPrecision > 0;

  const onKeyDown = event => {
    const { key } = event;
    if (isNumberInput) {
      if (_toLower(key) === 'e') {
        event.preventDefault();
      }
      if (!allowDecimal && key === '.') {
        event.preventDefault();
      }
    }
  };

  const onInputChange = event => {
    const value = event.target.value;
    if (isNumberInput) {
      const regex = allowDecimal
        ? new RegExp(`^\\d*([.])?\\d{0,${maxDecimalPrecision}}?$`)
        : /^\d*$/;
      const isValid = regex.test(value);
      if (!isValid) return;
    }
    onChange(value, event);
  };

  return (
    <div className={cn(styles.container, className)}>
      <UiKit.Label htmlFor={inputId} text={label} tooltipText={tooltipText}>
        <div className={cn(styles.inputWrapper, inputWrapperClassName)}>
          <input
            ref={inputRef}
            type={type}
            id={inputId}
            value={value}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            disabled={disabled}
            step={allowDecimal ? '0.1' : '1'}
            data-size={size}
            data-invalid={!!error}
            data-has-icon={!!icon}
            data-bypass-read-mode={bypassReadMode}
            className={cn(styles.input, inputClassName)}
            {...inputHtmlProps}
          />
          {icon && (
            <div className={styles.icon} data-disabled={disabled}>
              {icon}
            </div>
          )}
          {children}
        </div>
      </UiKit.Label>
      <UiKit.Error msg={error} hide={disabled} />
      <UiKit.Caption>{caption}</UiKit.Caption>
    </div>
  );
}
